import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { BookOpen, Loader2, Plus, Edit2, Trash2, Upload, FileUp, Download, ChevronLeft, ChevronRight, Layers, PackageCheck, Link2 as LinkIcon, Search, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    useAdminMasterScopus, useCreateMasterScopus, useUpdateMasterScopus, useDeleteMasterScopus,
    useAdminMasterSinta, useCreateMasterSinta, useUpdateMasterSinta, useDeleteMasterSinta,
    useImportMasterJournals, downloadOutputTemplate,
    type MasterScopusJournal, type MasterSintaJournal,
} from "@/services/adminConferenceOutputService";
import { StatCard } from "@/pages/private/admin/conference-output/OutputDecisionsPage";

const emptyScopus: Partial<MasterScopusJournal> = {
    external_ref_code: undefined, lptk_institution: "", journal_name: "", journal_url: "", quartile: "Q1",
    scope_description: "", editor_in_chief: "", request_note: "", slot_quota: undefined, slot_remaining: undefined,
    publisher: "", is_active: true,
};
const emptySinta: Partial<MasterSintaJournal> = {
    external_ref_code: undefined, lptk_institution: "", journal_name: "", journal_url: "", sinta_rank: "",
    scope_description: "", editor_in_chief: "", request_note: "", slot_quota: undefined, slot_remaining: undefined,
    publisher: "", is_active: true,
};

export default function OutputMasterJournalsPage() {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(t);
    }, [search]);

    const { data: scopusList, isLoading: loadingScopus } = useAdminMasterScopus(debouncedSearch);
    const { data: sintaList, isLoading: loadingSinta } = useAdminMasterSinta(debouncedSearch);
    const createScopus = useCreateMasterScopus();
    const updateScopus = useUpdateMasterScopus();
    const deleteScopus = useDeleteMasterScopus();
    const createSinta = useCreateMasterSinta();
    const updateSinta = useUpdateMasterSinta();
    const deleteSinta = useDeleteMasterSinta();
    const importMaster = useImportMasterJournals();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<"scopus" | "sinta">("scopus");
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<any>(emptyScopus);
    const [deleteConfirm, setDeleteConfirm] = useState<{ id: number; type: "scopus" | "sinta" } | null>(null);

    const [isImportOpen, setIsImportOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importResult, setImportResult] = useState<any>(null);

    const currentList = activeTab === "scopus" ? scopusList : sintaList;
    const stats = useMemo(() => {
        const list = currentList || [];
        return {
            total: list.length,
            active: list.filter((i: any) => i.is_active).length,
            totalQuota: list.reduce((sum: number, i: any) => sum + (i.slot_quota || 0), 0),
            totalRemaining: list.reduce((sum: number, i: any) => sum + (i.slot_remaining || 0), 0),
        };
    }, [currentList]);

    const openCreate = (type: "scopus" | "sinta") => {
        setActiveTab(type);
        setEditingId(null);
        setForm(type === "scopus" ? emptyScopus : emptySinta);
        setIsModalOpen(true);
    };

    const openEdit = (type: "scopus" | "sinta", item: any) => {
        setActiveTab(type);
        setEditingId(item.id);
        setForm({ ...item });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (activeTab === "scopus") {
                if (editingId) await updateScopus.mutateAsync({ id: editingId, payload: form });
                else await createScopus.mutateAsync(form);
            } else {
                if (editingId) await updateSinta.mutateAsync({ id: editingId, payload: form });
                else await createSinta.mutateAsync(form);
            }
            toast.success(editingId ? "Data berhasil diperbarui" : "Data berhasil ditambahkan");
            setIsModalOpen(false);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Terjadi kesalahan saat menyimpan data.");
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        try {
            if (deleteConfirm.type === "scopus") await deleteScopus.mutateAsync(deleteConfirm.id);
            else await deleteSinta.mutateAsync(deleteConfirm.id);
            toast.success("Data berhasil dihapus");
            setDeleteConfirm(null);
        } catch {
            toast.error("Gagal menghapus data");
        }
    };

    const handleImportSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!importFile) { toast.error("Pilih file Excel terlebih dahulu"); return; }
        const formData = new FormData();
        formData.append("file", importFile);
        importMaster.mutate(formData, {
            onSuccess: (data) => { setImportResult(data); toast.success(data.message || "Import berhasil"); },
            onError: (error: any) => toast.error(error.response?.data?.message || "Gagal import data"),
        });
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
                <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none"><BookOpen className="w-64 h-64 text-blue-600" /></div>
                <div className="flex items-start gap-5 relative z-10">
                    <div className="p-4 rounded-3xl bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 flex-shrink-0 border border-blue-100 dark:border-blue-800/50 shadow-sm"><BookOpen className="w-8 h-8" /></div>
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">Master Jurnal Scopus & Sinta</h1>
                        <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">Kelola daftar jurnal mitra beserta kuota slot per jurnal.</p>
                    </div>
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-3 relative z-10">
                    <Button onClick={() => { setIsImportOpen(true); setImportResult(null); setImportFile(null); }} variant="outline" className="h-12 px-6 rounded-xl font-semibold shadow-sm">
                        <Upload className="w-5 h-5 mr-2" /> Import Data
                    </Button>
                    <Button onClick={() => openCreate(activeTab)} className="h-12 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg shadow-blue-600/20">
                        <Plus className="w-5 h-5 mr-2" /> Tambah Jurnal
                    </Button>
                </div>
            </div>

            {/* Kartu Total (mengikuti tab yang sedang aktif) */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={BookOpen} label={`Total Jurnal ${activeTab === "scopus" ? "Scopus" : "Sinta"}`} value={stats.total} color="blue" />
                <StatCard icon={PackageCheck} label="Jurnal Aktif" value={stats.active} color="emerald" />
                <StatCard icon={Layers} label="Total Kuota Slot" value={stats.totalQuota} color="purple" />
                <StatCard icon={Layers} label="Sisa Slot" value={stats.totalRemaining} color="amber" />
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Search className="h-5 w-5 text-blue-500" /></div>
                    <Input
                        placeholder="Cari nama jurnal, institusi, editor, publisher, atau kode..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-12 pr-10 h-12 rounded-xl text-sm font-medium bg-slate-50 dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:ring-4 focus-visible:ring-blue-500/15 transition-all"
                    />
                    {search && (
                        <button type="button" onClick={() => setSearch("")} aria-label="Hapus pencarian" className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                            <XCircle className="h-5 w-5" />
                        </button>
                    )}
                </div>
                {debouncedSearch && (
                    <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 px-3 py-2 rounded-lg whitespace-nowrap">
                        {(currentList?.length ?? 0)} hasil untuk "{debouncedSearch}"
                    </span>
                )}
            </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
                <TabsList className="mb-6 h-auto w-full sm:w-auto p-1.5 gap-2 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-300 dark:border-slate-600 shadow-md grid grid-cols-2 sm:inline-grid">
                    {([
                        { value: "scopus", label: "Scopus", count: scopusList?.length },
                        { value: "sinta", label: "Sinta", count: sintaList?.length },
                    ] as const).map((t) => (
                        <TabsTrigger
                            key={t.value}
                            value={t.value}
                            className="h-12 sm:min-w-[180px] px-6 rounded-xl text-base font-bold gap-2.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-600/25 data-[state=active]:hover:bg-blue-600"
                        >
                            {t.label}
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${activeTab === t.value ? "bg-white/25 text-white" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}>
                                {t.count ?? 0}
                            </span>
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="scopus">
                    <JournalTable
                        key={`scopus-${debouncedSearch}`}
                        items={scopusList}
                        isLoading={loadingScopus}
                        rankLabel="Quartile"
                        rankKey="quartile"
                        onEdit={(item) => openEdit("scopus", item)}
                        onDelete={(id) => setDeleteConfirm({ id, type: "scopus" })}
                    />
                </TabsContent>
                <TabsContent value="sinta">
                    <JournalTable
                        key={`sinta-${debouncedSearch}`}
                        items={sintaList}
                        isLoading={loadingSinta}
                        rankLabel="Sinta Rank"
                        rankKey="sinta_rank"
                        onEdit={(item) => openEdit("sinta", item)}
                        onDelete={(id) => setDeleteConfirm({ id, type: "sinta" })}
                    />
                </TabsContent>
            </Tabs>

            {/* Create/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-lg p-0 overflow-hidden bg-white dark:bg-slate-900">
                    <DialogHeader className="p-6 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800">
                        <DialogTitle>{editingId ? "Edit Jurnal" : "Tambah Jurnal"} ({activeTab === "scopus" ? "Scopus" : "Sinta"})</DialogTitle>
                        <DialogDescription>Isi detail jurnal mitra dan kuota slot yang tersedia.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Kode Referensi (No) <span className="text-red-500">*</span></Label>
                                <Input type="number" value={form.external_ref_code ?? ""} onChange={(e) => setForm({ ...form, external_ref_code: e.target.value ? parseInt(e.target.value) : undefined })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>{activeTab === "scopus" ? "Quartile" : "Sinta Rank"} <span className="text-red-500">*</span></Label>
                                {activeTab === "scopus" ? (
                                    <Select value={form.quartile} onValueChange={(v) => setForm({ ...form, quartile: v })}>
                                        <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {["Q1", "Q2", "Q3", "Q4"].map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <Input value={form.sinta_rank ?? ""} onChange={(e) => setForm({ ...form, sinta_rank: e.target.value })} placeholder="Sinta 2" required />
                                )}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Nama Jurnal <span className="text-red-500">*</span></Label>
                            <Input value={form.journal_name ?? ""} onChange={(e) => setForm({ ...form, journal_name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Link Jurnal</Label>
                            <Input type="url" value={form.journal_url ?? ""} onChange={(e) => setForm({ ...form, journal_url: e.target.value })} placeholder="https://..." />
                        </div>
                        <div className="space-y-2">
                            <Label>Institusi (LPTK)</Label>
                            <Input value={form.lptk_institution ?? ""} onChange={(e) => setForm({ ...form, lptk_institution: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Editor in Chief</Label>
                            <Input value={form.editor_in_chief ?? ""} onChange={(e) => setForm({ ...form, editor_in_chief: e.target.value })} />
                        </div>
                        <div className="space-y-2">
                            <Label>Cakupan / Scope</Label>
                            <Textarea value={form.scope_description ?? ""} onChange={(e) => setForm({ ...form, scope_description: e.target.value })} className="h-20" />
                        </div>
                        <div className="space-y-2">
                            <Label>Catatan Request</Label>
                            <Textarea value={form.request_note ?? ""} onChange={(e) => setForm({ ...form, request_note: e.target.value })} className="h-16" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Kuota Slot</Label>
                                <Input type="number" value={form.slot_quota ?? ""} onChange={(e) => setForm({ ...form, slot_quota: e.target.value ? parseInt(e.target.value) : null })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Sisa Slot</Label>
                                <Input type="number" value={form.slot_remaining ?? ""} onChange={(e) => setForm({ ...form, slot_remaining: e.target.value ? parseInt(e.target.value) : null })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Publisher</Label>
                            <Input value={form.publisher ?? ""} onChange={(e) => setForm({ ...form, publisher: e.target.value })} />
                        </div>
                        <div className="flex items-center justify-between pt-2">
                            <Label>Aktif</Label>
                            <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                        </div>
                        <DialogFooter className="pt-4 mt-2 border-t border-slate-200 dark:border-slate-800">
                            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">Batal</Button>
                            <Button type="submit" disabled={createScopus.isPending || updateScopus.isPending || createSinta.isPending || updateSinta.isPending} className="rounded-xl bg-blue-600 hover:bg-blue-700">
                                {(createScopus.isPending || updateScopus.isPending || createSinta.isPending || updateSinta.isPending) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Simpan
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirm */}
            <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
                <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900">
                    <DialogHeader>
                        <DialogTitle>Hapus Jurnal?</DialogTitle>
                        <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-6">
                        <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Batal</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={deleteScopus.isPending || deleteSinta.isPending}>
                            {(deleteScopus.isPending || deleteSinta.isPending) ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : "Ya, Hapus"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Modal */}
            <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
                <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-900 rounded-3xl p-0 overflow-hidden">
                    <DialogHeader className="p-6 pb-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                        <DialogTitle className="flex items-center gap-3"><div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600"><Upload className="w-5 h-5" /></div>Import Master Jurnal</DialogTitle>
                        <DialogDescription>Import file "0 Pemetaan Target Jurnal - Track.xlsx" (sheet Scopus & Sinta).</DialogDescription>
                    </DialogHeader>
                    {importResult ? (
                        <div className="p-6">
                            <p className="font-semibold text-emerald-700 dark:text-emerald-400">{importResult.message}</p>
                            <div className="mt-6 flex justify-end">
                                <Button onClick={() => setIsImportOpen(false)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">Tutup</Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleImportSubmit} className="p-6 flex flex-col gap-6">
                            <div className="relative group">
                                <div className="absolute inset-0 bg-blue-50 dark:bg-blue-900/10 border-2 border-dashed border-blue-200 dark:border-blue-800/50 rounded-2xl group-hover:border-blue-400 transition-colors pointer-events-none flex flex-col items-center justify-center p-6 gap-2">
                                    <FileUp className="w-6 h-6 text-blue-500" />
                                    <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm">{importFile ? importFile.name : "Pilih file Excel"}</p>
                                    <p className="text-xs text-slate-500">.xlsx atau .xls</p>
                                </div>
                                <Input type="file" accept=".xlsx,.xls" onChange={(e) => setImportFile(e.target.files?.[0] || null)} className="w-full h-[130px] opacity-0 cursor-pointer" />
                            </div>
                            <Button variant="link" size="sm" className="px-0 -mt-4" type="button" onClick={() => downloadOutputTemplate("master-journals", "template-master-journals.xlsx")}>
                                <Download className="w-4 h-4 mr-1" /> Unduh Template Excel
                            </Button>
                            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                <Button type="button" variant="outline" onClick={() => setIsImportOpen(false)} className="rounded-xl">Batal</Button>
                                <Button type="submit" disabled={importMaster.isPending || !importFile} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white">
                                    {importMaster.isPending ? "Memproses..." : "Mulai Import"}
                                </Button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function JournalTable({ items, isLoading, rankLabel, rankKey, onEdit, onDelete }: {
    items: any[] | undefined;
    isLoading: boolean;
    rankLabel: string;
    rankKey: string;
    onEdit: (item: any) => void;
    onDelete: (id: number) => void;
}) {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const totalItems = items?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / perPage));
    const startIndex = (page - 1) * perPage;
    const endIndex = Math.min(startIndex + perPage, totalItems);
    const currentItems = (items ?? []).slice(startIndex, endIndex);

    return (
        <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse min-w-[700px]">
                    <thead>
                        <tr className="bg-slate-50/80 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm">
                            <th className="px-6 py-4 font-semibold w-16 text-center">No</th>
                            <th className="px-6 py-4 font-semibold">Nama Jurnal</th>
                            <th className="px-6 py-4 font-semibold text-center">{rankLabel}</th>
                            <th className="px-6 py-4 font-semibold text-center">Slot</th>
                            <th className="px-6 py-4 font-semibold text-center">Status</th>
                            <th className="px-6 py-4 font-semibold text-right w-24 sticky right-0 bg-slate-50/80 dark:bg-slate-800/50 backdrop-blur-sm z-10 border-l border-slate-200 dark:border-slate-800 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800/60">
                        {isLoading ? (
                            <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />Memuat data...</td></tr>
                        ) : totalItems === 0 ? (
                            <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-500">Data tidak ditemukan.</td></tr>
                        ) : (
                            currentItems.map((item, idx) => (
                                <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                    <td className="px-6 py-4 text-center text-slate-500">{item.external_ref_code ?? startIndex + idx + 1}</td>
                                    <td className="px-6 py-4">
                                        <div className="font-bold text-slate-900 dark:text-white text-sm">{item.journal_name}</div>
                                        {item.editor_in_chief && <div className="text-xs text-slate-500">EiC: {item.editor_in_chief}</div>}
                                        {item.journal_url && (
                                            <a href={item.journal_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1 mt-0.5">
                                                <LinkIcon className="w-3 h-3" /> Lihat jurnal
                                            </a>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="text-[11px] font-bold uppercase px-2.5 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 rounded-md">{item[rankKey] || "-"}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center text-sm text-slate-600 dark:text-slate-300">{item.slot_remaining ?? "-"} / {item.slot_quota ?? "-"}</td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`text-[11px] font-bold uppercase px-2.5 py-1 rounded-md ${item.is_active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800"}`}>
                                            {item.is_active ? "Aktif" : "Nonaktif"}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right sticky right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm z-10 border-l border-slate-100 dark:border-slate-800/60 shadow-[-4px_0_10px_rgba(0,0,0,0.02)] dark:shadow-[-4px_0_10px_rgba(0,0,0,0.2)]">
                                        <div className="flex items-center justify-end gap-1">
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-8 w-8 bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400"><Edit2 className="w-4 h-4" /></Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Edit Jurnal</p></TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={() => onDelete(item.id)} className="h-8 w-8 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Hapus Jurnal</p></TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {totalItems > 0 && !isLoading && (
                <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                    <span className="text-slate-500 font-medium text-xs sm:text-sm">
                        Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
                    </span>
                    <div className="flex items-center gap-4 sm:gap-6">
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                            <select
                                value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-8 px-3 rounded-lg"><ChevronLeft className="w-4 h-4" /></Button>
                            <span className="text-slate-600 dark:text-slate-400 font-medium min-w-[3rem] text-center">{page} / {totalPages}</span>
                            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="h-8 px-3 rounded-lg"><ChevronRight className="w-4 h-4" /></Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
